import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Box, CircularProgress, Snackbar } from "@mui/material";
import { pm, errorMessage } from "./api";
import { getAgentSocket } from "../helpdesk/socket";
import { PageShell, T } from "../user/complaints/shared";

// Workspace PM metadata (types, statuses, priorities, roles, people, teams,
// my workspace permissions) loaded once per page and shared by every screen.

const Ctx = createContext(null);
export const usePm = () => useContext(Ctx);

export const PmProvider = ({ children }) => {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  const reload = useCallback(async () => {
    try {
      setMeta(await pm.meta());
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load project management"));
    }
  }, []);

  useEffect(() => {
    reload();
    getAgentSocket();
  }, [reload]);

  const notify = useCallback((message, severity = "success") => setToast({ message, severity, key: Date.now() }), []);

  const value = useMemo(() => {
    if (!meta) return null;
    const byId = (list) => new Map((list || []).map((x) => [String(x._id), x]));
    const perms = new Set(meta.me.permissions);
    return {
      ...meta,
      canWs: (p) => meta.me.isAdmin || perms.has(p),
      // Create project: admins and account managers only.
      canCreateProject: Boolean(meta.me.isManager) && (meta.me.isAdmin || perms.has("project.create")),
      typeById: byId(meta.issueTypes),
      typeByKey: new Map(meta.issueTypes.map((t) => [t.key, t])),
      statusById: byId(meta.statuses),
      priorityById: byId(meta.priorities),
      linkTypeById: byId(meta.linkTypes),
      roleById: byId(meta.roles),
      teamById: byId(meta.teams),
      personById: new Map(meta.people.map((p) => [String(p.id), p])),
      projectRoles: meta.roles.filter((r) => r.scope === "project"),
      workspaceRoles: meta.roles.filter((r) => r.scope === "workspace"),
      reload,
      notify,
      fail: (err, fallback) => notify(errorMessage(err, fallback), "error"),
    };
  }, [meta, reload, notify]);

  return (
    <>
      {error && !meta ? (
        <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>
      ) : !value ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
          <CircularProgress size={28} sx={{ color: T.brand }} />
        </Box>
      ) : (
        <Ctx.Provider value={value}>{children}</Ctx.Provider>
      )}
      <Snackbar open={Boolean(toast)} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        {toast ? <Alert key={toast.key} severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.message}</Alert> : <span />}
      </Snackbar>
    </>
  );
};

/** Page frame: CRM sidebar + PM metadata. */
export const PmPage = ({ children, maxWidth = "xl" }) => (
  <PageShell maxWidth={maxWidth}>
    <PmProvider>{children}</PmProvider>
  </PageShell>
);
